import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { Button, Text } from '~/components/core';
import { Ionicons } from '~/components/core/Icons';
import { useStyles } from '~/hooks';
import { useTheme } from '~/hooks/useTheme';
import i18n from '~/i18n';
import { FONT_SIZE_S, FONT_SIZE_XS } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

import { CreateStrategyWizardApi } from '../../../hooks/useCreateStrategyWizard';
import RuleEditor from '../RuleEditor';

type Props = {
    wizard: CreateStrategyWizardApi;
};

// Step 3: entry rules as summary cards; add/edit opens the full-screen RuleEditor. How many
// rules fit is per-account (`wizard.maxRules`).
export default function StepRules({ wizard }: Props) {
    const styles = useStyles(stylesGenerator);
    const theme = useTheme();
    // Index of the rule being edited, or null. -1 means "add then edit the new last rule".
    const [editing, setEditing] = useState<number | null>(null);

    const noCombinations = wizard.optionsAtSlot([], 0).length === 0;

    const startAdd = () => {
        wizard.addRule();
        setEditing(wizard.rules.length); // the freshly appended rule's index
    };

    if (noCombinations) {
        return <Text style={styles.emptyText}>ai-strategy-rules-none-available</Text>;
    }

    return (
        <View>
            <Text style={styles.introText}>ai-strategy-rules-intro</Text>

            {wizard.rules.map((rule, i) => {
                const combo = wizard.combinationFor(rule);
                const violations = wizard.violationsFor(rule);
                const complete = wizard.ruleValid(rule);
                return (
                    <Pressable key={i} style={styles.card} onPress={() => setEditing(i)}>
                        <View style={styles.cardHeader}>
                            <Text noTranslate fontType="LATO_BOLD" style={styles.cardTitle}>
                                {`${i + 1}. ${
                                    wizard.ruleCodesForDisplay(rule).map(wizard.instanceLabel).join(' + ') || '—'
                                }`}
                            </Text>
                            <View style={styles.cardActions}>
                                {wizard.rules.length > 1 ? (
                                    <Pressable
                                        hitSlop={8}
                                        onPress={() => wizard.removeRule(i)}
                                        accessibilityLabel="Remove rule"
                                    >
                                        <Ionicons name="close" size={scaleSize(16)} style={styles.removeIcon} />
                                    </Pressable>
                                ) : null}
                            </View>
                        </View>
                        {!complete ? <Text style={styles.incompleteText}>ai-strategy-rule-incomplete</Text> : null}
                        {combo
                            ? combo.connections.map((conn, ci) => (
                                  <Text noTranslate key={ci} style={styles.conditionText}>
                                      {wizard.describeConnection(conn)}
                                  </Text>
                              ))
                            : null}
                        {violations.map((v, vi) => (
                            <Text noTranslate key={`v-${vi}`} style={styles.violationText}>
                                {v}
                            </Text>
                        ))}
                    </Pressable>
                );
            })}

            {wizard.canAddRule ? (
                <View style={styles.addWrap}>
                    <Button
                        title="ai-strategy-add-rule"
                        onPress={startAdd}
                        backgroundColor={{ backgroundColor: theme.backgroundSecondary }}
                        titleColor={theme.text}
                    />
                </View>
            ) : (
                <Text noTranslate style={styles.limitText}>
                    {i18n.t('ai-strategy-rules-limit', { count: wizard.maxRules })}
                </Text>
            )}

            {editing !== null && wizard.rules[editing] ? (
                <RuleEditor
                    visible
                    initialRule={wizard.rules[editing]}
                    ruleIndex={editing}
                    wizard={wizard}
                    onSave={(rule) => {
                        wizard.updateRule(editing, rule);
                        setEditing(null);
                    }}
                    onCancel={() => {
                        // Discard an added-but-never-configured trailing rule.
                        const rule = wizard.rules[editing];
                        if (rule.codes.length === 0 && wizard.rules.length > 1) {
                            wizard.removeRule(editing);
                        }
                        setEditing(null);
                    }}
                />
            ) : null}
        </View>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        introText: {
            fontSize: FONT_SIZE_S,
            color: theme.textSecondary,
            marginTop: scaleSize(12),
            marginBottom: scaleSize(8),
        },
        emptyText: {
            fontSize: FONT_SIZE_S,
            color: theme.textSecondary,
            marginTop: scaleSize(16),
            textAlign: 'center',
        },
        card: {
            backgroundColor: theme.backgroundSecondary,
            borderRadius: scaleSize(8),
            paddingHorizontal: scaleSize(12),
            paddingVertical: scaleSize(10),
            marginBottom: scaleSize(8),
        },
        cardHeader: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: scaleSize(4),
        },
        cardTitle: {
            flex: 1,
            fontSize: FONT_SIZE_S,
            color: theme.text,
            marginRight: scaleSize(8),
        },
        cardActions: {
            flexDirection: 'row',
            alignItems: 'center',
        },
        incompleteText: {
            fontSize: FONT_SIZE_XS,
            color: theme.alert,
            marginTop: scaleSize(2),
        },
        removeIcon: {
            color: theme.textSecondary,
        },
        conditionText: {
            fontSize: FONT_SIZE_XS,
            color: theme.text,
            marginTop: scaleSize(2),
        },
        violationText: {
            fontSize: FONT_SIZE_XS,
            color: theme.alert,
            marginTop: scaleSize(2),
        },
        addWrap: {
            marginTop: scaleSize(4),
        },
        limitText: {
            fontSize: FONT_SIZE_XS,
            color: theme.textSecondary,
            marginTop: scaleSize(4),
            textAlign: 'center',
        },
    });
